import React, { useEffect, useState, useCallback, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Loader, ErrorBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as communicationApi from '../../api/communication';
import * as projectsApi from '../../api/projects';
import { formatDateTime } from '../../utils/format';

export default function Communication() {
  const { actor } = useAuth();
  const [channelType, setChannelType] = useState('team');
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    projectsApi.listProjects({ limit: 200 }).then((res) => {
      setProjectOptions(res.data);
      if (res.data.length > 0) setProjectId(res.data[0].id);
    });
  }, []);

  const load = useCallback(() => {
    if (channelType === 'project' && !projectId) return;
    setLoading(true);
    communicationApi
      .listMessages({ channelType, projectId: channelType === 'project' ? projectId : undefined })
      .then((res) => setMessages(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load messages.'))
      .finally(() => setLoading(false));
  }, [channelType, projectId]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 8000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await communicationApi.sendMessage({ message: text.trim(), channelType, projectId: channelType === 'project' ? projectId : undefined });
      setText('');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send message.');
    }
  };

  return (
    <DashboardLayout title="Team Chat">
      <div className="page-header">
        <div>
          <h1 className="heading">Internal Communication</h1>
          <p className="page-subtext">Replaces ad-hoc chat apps with a channel tied to the firm and your projects.</p>
        </div>
      </div>

      <div className="tab-bar">
        <button type="button" className={`tab-item${channelType === 'team' ? ' active' : ''}`} onClick={() => setChannelType('team')}>Team Channel</button>
        <button type="button" className={`tab-item${channelType === 'project' ? ' active' : ''}`} onClick={() => setChannelType('project')}>Project Channel</button>
      </div>

      {channelType === 'project' && (
        <div className="filter-bar">
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            {projectOptions.map((p) => <option key={p.id} value={p.id}>{p.projectName}</option>)}
          </select>
        </div>
      )}

      <ErrorBanner message={error} />

      <div className="card" style={{ height: 420, display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && <Loader text="Loading messages..." />}
          {!loading && messages.length === 0 && <p className="text-muted">No messages yet. Say hello.</p>}
          {!loading && messages.map((m) => (
            <div key={m.id} style={{ marginBottom: 10 }}>
              <div className="comment-meta">
                <strong>{m.sender?.name}</strong>{m.senderId === actor.profile.id ? ' (you)' : ''} &middot; {formatDateTime(m.createdAt)}
              </div>
              <div>{m.message}</div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        <form onSubmit={handleSend} style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <input type="text" placeholder="Type a message... use @Name to mention someone" value={text} onChange={(e) => setText(e.target.value)} />
          <button type="submit" className="btn">Send</button>
        </form>
      </div>
    </DashboardLayout>
  );
}
